import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';

import Button from '../../../../components/Button/Button';
import Counter from '../../../../components/Counter/Counter';
import { InfoIcon, PlusCircleIcon, TrashIcon } from '../../../../components/Icon/Icon';
import SelectDropdown from '../../../../components/SelectDropdown/SelectDropdown';
import ToggleSwitch from '../../../../components/ToggleSwitch/ToggleSwitch';
import Tooltip from '../../../../components/Tooltip/Tooltip';
import { TRANSPORTS } from '../../../../declarations/constants/transports';
import { TransportTypesE } from '../../../../declarations/enums/common';
import TransportSelect from '../TransportSelect/TransportSelect';
import useTransportationForm from './hooks/useTransportationForm';
import {
  ColStyled,
  ControlsWrapperStyled,
  DestinationWrapperStyled,
  FormRowStyled,
  LabelStyled,
  NoOptionsWrapperStyled,
  OffsetButtonWrapperStyled,
  RowsWrapperStyled,
  SegmentRowStyled,
  ToggleWrapperStyled,
  TransportationFormWrapperStyled,
  TransportsWrapperStyled,
  WarningStyled,
} from './TransportationForm.styled';

type TransportationFormPropsT = {
  isTriggeredBreadcrumbs: boolean;
};

const TransportationForm: React.FC<TransportationFormPropsT> = ({ isTriggeredBreadcrumbs }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const {
    isAddClicked,
    hasErrors,
    locations,
    segments,
    setValue,
    onClickOffset,
    handleOnChangeLocation,
    handleAddNewSegment,
    handleOnDeleteSegment,
    handleChangeSegment,
  } = useTransportationForm();

  useEffect(() => {
    if (!hasErrors && isTriggeredBreadcrumbs) {
      onClickOffset();
    }
  }, [isTriggeredBreadcrumbs]);

  return (
    <TransportationFormWrapperStyled>
      {segments.map(
        (
          {
            roundTrip,
            transportType: selectedTransportType,
            vehicleType,
            origin: { name: originName },
            destination: { name: destinationName },
            numberOfPassengers,
            numberOfVehicles,
          },
          segmentIndex
        ) => {
          const showTravelersQtyField =
            [TransportTypesE.TRAIN, TransportTypesE.PLANE].includes(
              selectedTransportType as TransportTypesE
            ) || ['FERRY_NO_VEHICLES', 'FERRY_WITH_VEHICLES'].includes(vehicleType);
          const showNumberOfVehicles = vehicleType === 'FERRY_WITH_VEHICLES';
          const isRequiredEmpty = !vehicleType || !originName || !destinationName;

          return (
            <SegmentRowStyled key={segmentIndex}>
              <TransportsWrapperStyled key={segmentIndex}>
                <TransportsWrapperStyled>
                  {TRANSPORTS.map(({ vehicles, transportType }, index) => (
                    <TransportSelect
                      key={index}
                      vehicles={vehicles}
                      transportType={transportType}
                      vehicleTypeValue={
                        selectedTransportType === transportType
                          ? vehicles.find(({ code }) => code === vehicleType)
                          : null
                      }
                      onSelect={val => handleChangeSegment(val, segmentIndex)}
                    />
                  ))}
                  <ToggleWrapperStyled>
                    <ToggleSwitch
                      id="round-trip"
                      name="roundTrip"
                      checked={roundTrip}
                      onChange={val => handleChangeSegment({ roundTrip: val }, segmentIndex)}
                    />

                    <FormattedMessage id="trip.roundTrip" defaultMessage="Roundtrip" />
                  </ToggleWrapperStyled>

                  {isAddClicked && !selectedTransportType && (
                    <WarningStyled>
                      <FormattedMessage
                        id="trip.chooseType.validation"
                        defaultMessage="Please Choose a Transport Type"
                      />
                    </WarningStyled>
                  )}
                </TransportsWrapperStyled>
              </TransportsWrapperStyled>

              <DestinationWrapperStyled>
                <RowsWrapperStyled>
                  <FormRowStyled>
                    <ColStyled>
                      {!segmentIndex && (
                        <LabelStyled>
                          <FormattedMessage id="form.from" defaultMessage="From" />
                        </LabelStyled>
                      )}

                      <SelectDropdown
                        isSearchable
                        isClearable
                        noOptionsMessage={() => (
                          <NoOptionsWrapperStyled>
                            <FormattedMessage id="form.noOptions" defaultMessage="Enter Address" />
                          </NoOptionsWrapperStyled>
                        )}
                        value={
                          originName
                            ? {
                                value: originName,
                                label: originName,
                              }
                            : undefined
                        }
                        isError={isAddClicked && !originName}
                        isDisabled={!!segmentIndex}
                        onInputChange={val => setValue(val)}
                        onChange={val => {
                          handleOnChangeLocation(val?.value as string, 'origin', segmentIndex);
                        }}
                        options={locations}
                        inputIconUrl="/images/icons/search.svg"
                        placeholder={formatMessage({
                          id: 'placeholder.startingPoint',
                          defaultMessage: 'Starting point',
                        })}
                      />
                    </ColStyled>

                    <ColStyled>
                      {!segmentIndex && (
                        <LabelStyled>
                          <FormattedMessage id="form.to" defaultMessage="To" />
                        </LabelStyled>
                      )}

                      <SelectDropdown
                        isSearchable
                        isClearable
                        value={
                          destinationName
                            ? {
                                value: destinationName,
                                label: destinationName,
                              }
                            : undefined
                        }
                        isError={isAddClicked && !destinationName}
                        onInputChange={val => setValue(val)}
                        onChange={option =>
                          handleOnChangeLocation(
                            option?.value as string,
                            'destination',
                            segmentIndex
                          )
                        }
                        options={locations}
                        noOptionsMessage={() => (
                          <NoOptionsWrapperStyled>
                            <FormattedMessage id="form.noOptions" defaultMessage="Enter Address" />
                          </NoOptionsWrapperStyled>
                        )}
                        inputIconUrl="/images/icons/search.svg"
                        placeholder={formatMessage({
                          id: 'placeholder.destination',
                          defaultMessage: 'Destination',
                        })}
                      />
                    </ColStyled>
                  </FormRowStyled>

                  <FormRowStyled>
                    {showTravelersQtyField && (
                      <ColStyled>
                        <LabelStyled>
                          <FormattedMessage
                            id="form.numberOfTravelers"
                            defaultMessage="Number of travelers"
                          />
                        </LabelStyled>
                        <Counter
                          value={numberOfPassengers}
                          max={300}
                          onChange={val =>
                            handleChangeSegment({ numberOfPassengers: val }, segmentIndex)
                          }
                        />
                      </ColStyled>
                    )}

                    {showNumberOfVehicles && (
                      <ColStyled>
                        <LabelStyled>
                          <FormattedMessage
                            id="form.numberOfVehicles"
                            defaultMessage="Number of vehicles"
                          />
                        </LabelStyled>

                        <Counter
                          value={numberOfVehicles}
                          max={10}
                          onChange={val =>
                            handleChangeSegment({ numberOfVehicles: val }, segmentIndex)
                          }
                        />
                      </ColStyled>
                    )}
                  </FormRowStyled>
                </RowsWrapperStyled>

                <ControlsWrapperStyled>
                  {!!segmentIndex && segmentIndex === segments.length - 1 && (
                    <Button modifier="inline" onClick={() => handleOnDeleteSegment(segmentIndex)}>
                      <TrashIcon />
                    </Button>
                  )}

                  {segmentIndex === segments.length - 1 && (
                    <>
                      <Button
                        modifier="inline"
                        onClick={() => handleAddNewSegment(isRequiredEmpty)}
                      >
                        <PlusCircleIcon />
                      </Button>

                      <Tooltip element={<InfoIcon color={theme.primaryColour} />}>
                        <FormattedMessage
                          id="plusSign.tooltip.text"
                          defaultMessage="This + symbol allows you to add your transportation type for every stop or transfer point along your trip. Select all that apply."
                        />
                      </Tooltip>
                    </>
                  )}
                </ControlsWrapperStyled>
              </DestinationWrapperStyled>
            </SegmentRowStyled>
          );
        }
      )}

      <OffsetButtonWrapperStyled>
        <Button isInverted disabled={hasErrors} onClick={onClickOffset}>
          <FormattedMessage id="common.offset" defaultMessage="Offset" />
        </Button>
      </OffsetButtonWrapperStyled>
    </TransportationFormWrapperStyled>
  );
};

export default TransportationForm;
